import dayjs from 'dayjs';
import { dayjsLocalizer } from 'react-big-calendar';

import type { IShowing } from 'src/api/landlord-showings-api';
import type { IProperty } from 'src/services/api';
import { parseDateTimeInUserTimezone } from 'src/utils/format-date-time';
import {
    CANCELED_TOUR_COLOR,
    getPropertyColor,
} from 'src/pages/landlord/tours/components/calendar/property-colors';

const calendarDayjs = ((value?: dayjs.ConfigType) =>
    dayjs(value)) as typeof dayjs;

calendarDayjs.extend = dayjs.extend.bind(dayjs);
calendarDayjs.localeData = () => dayjs.localeData();
calendarDayjs.min = dayjs.min.bind(dayjs);
calendarDayjs.max = dayjs.max.bind(dayjs);

// The application Day.js instance has the timezone plugin. React Big Calendar
// detects a static `.tz` method and then shifts its grid arithmetic into that
// timezone. This adapter intentionally has no `.tz`; events are converted to
// user-timezone wall clock explicitly by `toCalendarWallClockDate`.
export const tourCalendarLocalizer = dayjsLocalizer(calendarDayjs);

export interface ITourCalendarEvent {
    title: string;
    start: Date;
    end: Date;
    showing: IShowing;
}

export interface IPropertyCalendarMaps {
    propertyNameById: Map<number, string>;
    propertyColorById: Map<number, string>;
    rootPropertyIdById: Map<number, number>;
}

/**
 * Colors are assigned per root parent property (building) rather than per
 * property, so tours booked on different units of the same building share one
 * color on the calendar and in the sidebar legend.
 *
 * :param properties: the team's flat property list (parents and children)
 * :returns: id-keyed lookup maps for name, display color and root ancestor id
 */
export const buildPropertyCalendarMaps = (
    properties: IProperty[]
): IPropertyCalendarMaps => {
    const propertyById = new Map<number, IProperty>();
    properties.forEach((property) => {
        if (property) {
            propertyById.set(property.id, property);
        }
    });

    // Guards against orphaned parent ids and cyclic parent links, both of
    // which would otherwise loop forever; the walk stops at the last property
    // that exists in the list.
    const resolveRootPropertyId = (property: IProperty): number => {
        let current = property;
        const visitedIds = new Set<number>([current.id]);
        while (current.parent_property_id !== null) {
            const parent = propertyById.get(current.parent_property_id);
            if (!parent || visitedIds.has(parent.id)) {
                break;
            }
            visitedIds.add(parent.id);
            current = parent;
        }
        return current.id;
    };

    const propertyNameById = new Map<number, string>();
    const rootPropertyIdById = new Map<number, number>();
    const colorByRootId = new Map<number, string>();

    properties.forEach((property) => {
        if (!property) {
            return;
        }
        propertyNameById.set(property.id, property.name);
        const rootId = resolveRootPropertyId(property);
        rootPropertyIdById.set(property.id, rootId);
        if (!colorByRootId.has(rootId)) {
            colorByRootId.set(rootId, getPropertyColor(colorByRootId.size));
        }
    });

    const propertyColorById = new Map<number, string>();
    rootPropertyIdById.forEach((rootId, propertyId) => {
        propertyColorById.set(propertyId, colorByRootId.get(rootId)!);
    });

    return { propertyNameById, propertyColorById, rootPropertyIdById };
};

export const NO_PROPERTY_LABEL = 'No property';

export interface ITourLegendChild {
    propertyId: number;
    name: string;
}

export interface ITourLegendGroup {
    /** Null groups tours whose property id is unknown to the property list. */
    rootPropertyId: number | null;
    name: string;
    color: string;
    children: ITourLegendChild[];
    /** Every property id in the group, for select/deselect-all toggling. */
    propertyIds: number[];
}

/**
 * Groups the legend's visible property ids by root parent property. Groups and
 * their children are ordered alphabetically, with the unknown-property group
 * last. Tours booked directly on the root property count toward the group (and
 * its toggling) but get no child row of their own.
 *
 * :param visiblePropertyIds: property ids to show in the legend
 * :param maps: lookup maps from :func:`buildPropertyCalendarMaps`
 * :returns: legend groups ready to render
 */
export const buildTourLegendGroups = (
    visiblePropertyIds: Set<number>,
    maps: IPropertyCalendarMaps
): ITourLegendGroup[] => {
    const groups = new Map<number | null, ITourLegendGroup>();

    visiblePropertyIds.forEach((propertyId) => {
        const rootId = maps.rootPropertyIdById.get(propertyId) ?? null;
        let group = groups.get(rootId);
        if (!group) {
            group = {
                rootPropertyId: rootId,
                name: rootId === null ?
                    NO_PROPERTY_LABEL :
                    maps.propertyNameById.get(rootId) ?? 'Property',
                color: rootId === null ?
                    CANCELED_TOUR_COLOR :
                    maps.propertyColorById.get(rootId) ?? CANCELED_TOUR_COLOR,
                children: [],
                propertyIds: [],
            };
            groups.set(rootId, group);
        }
        group.propertyIds.push(propertyId);
        if (rootId !== null && propertyId !== rootId) {
            group.children.push({
                propertyId,
                name: maps.propertyNameById.get(propertyId) ?? 'Property',
            });
        }
    });

    return [...groups.values()]
        .map((group) => ({
            ...group,
            children: [...group.children].sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => {
            if (a.rootPropertyId === null) {
                return 1;
            }
            if (b.rootPropertyId === null) {
                return -1;
            }
            return a.name.localeCompare(b.name);
        });
};

export const toCalendarWallClockDate = (
    isoString: string,
    timezone: string
): Date => {
    return new Date(
        parseDateTimeInUserTimezone(isoString, timezone)
            .format('YYYY-MM-DDTHH:mm:ss')
    );
};

/**
 * Fetch window for the calendar covering exactly the visible period, so every
 * rendered cell is populated and nothing overflows uncovered:
 *
 * - Week view: the whole week ``[startOf('week'), startOf next week)``.
 * - Month view: the whole 6-week grid the month renders, i.e. from the start of
 *   the week containing the 1st to the start of the week after the last shown.
 * - Day view: that single day.
 *
 * Returned as naive (offset-stripped) ISO strings, matching the tours API
 * contract. The window is aligned to whole weeks (or a day in day view), so
 * navigating between weeks/months produces a fresh window for that period.
 */
export const getCalendarFetchWindow = (
    date: Date,
    view: 'month' | 'week' | 'day'
): { starts_after: string; starts_before: string } => {
    const focus = dayjs(date);
    const gridStart = view === 'month' ?
        focus.startOf('month').startOf('week') :
        focus.startOf(view);
    const gridEnd = view === 'month' ?
        focus.endOf('month').endOf('week') :
        focus.endOf(view);
    return {
        starts_after: gridStart.format('YYYY-MM-DDTHH:mm:ss'),
        starts_before: gridEnd.add(1, 'millisecond').format('YYYY-MM-DDTHH:mm:ss'),
    };
};

/**
 * Why a tour cannot be edited, or `null` when it can. Mirrors the backend's
 * `validate_can_edit_tour` rules so the UI gates the same actions the server does.
 */
export type TourEditBlockReason = 'not_owner' | 'canceled';

/**
 * Whether the current user may edit/confirm/cancel/mark a tour. A tour is editable
 * only by the owner of its lead, and only while it is not canceled. Canceled tours
 * stay visible but are read-only.
 *
 * :param tour: the tour (only ``is_canceled`` is read)
 * :param leadOwnerId: the id of the user who owns the tour's lead
 * :param currentUserId: the logged-in user's id
 * :returns: the reason editing is blocked, or ``null`` if editing is allowed
 */
export const getTourEditBlockReason = (
    tour: Pick<IShowing, 'is_canceled'>,
    leadOwnerId: number | undefined,
    currentUserId: number | undefined
): TourEditBlockReason | null => {
    if (leadOwnerId === undefined || currentUserId === undefined || leadOwnerId !== currentUserId) {
        return 'not_owner';
    }
    if (tour.is_canceled) {
        return 'canceled';
    }
    return null;
};

/** Convenience boolean form of :func:`getTourEditBlockReason`. */
export const canEditTour = (
    tour: Pick<IShowing, 'is_canceled'>,
    leadOwnerId: number | undefined,
    currentUserId: number | undefined
): boolean => getTourEditBlockReason(tour, leadOwnerId, currentUserId) === null;

export const getCalendarDayTimes = (
    date: Date,
    scrollHour: number
): { min: Date; max: Date; scrollToTime: Date } => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return {
        min: new Date(year, month, day, 0, 0, 0, 0),
        max: new Date(year, month, day, 23, 59, 59, 999),
        scrollToTime: new Date(year, month, day, scrollHour, 0, 0, 0),
    };
};
